import { FeatureFlagIds } from "../../../../../utils/featureFlagIds";

import React from 'react';
import { ComboBox, ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { DatePickerChangeEvent } from '@progress/kendo-react-dateinputs';
import {Input, NumericTextBoxChangeEvent, TextArea} from '@progress/kendo-react-inputs';
import styles from './_financing-editor.module.scss';
import {Error} from "@progress/kendo-react-labels";
import {
  FinanceLoanClass, FinanceQuote,
  FinanceQuoteNonUniformPaymentOption, getEnumForValue, getOptionsFromEnumAndLoanClass,
  hasValue, NoScrollNumericTextBox,
  toDollars,
  DatePickerWithYearSanitizer
} from "@amsanalytics/react-shared-library";
import FinanceQuoterDownPaymentOption from "../../FinanceQuoterDownPaymentOption/FinanceQuoterDownPaymentOption";
import {IApplication} from "../../../../../Interfaces/Application/IApplication";
import {IConfiguration} from "../../../../../Data/ConfigurationData";
import {IRuleViolation} from "../../../../../Interfaces/Application/IApplicationQuote";
import {ILookupItems} from "../../../../../Interfaces/ILookupItems";
import {IEmployee} from "../../../../../Interfaces/Admin/IEmployee";
import {
  getBackgroundColorForPotentialSolveForFields,
  getFinanceQuoteErrorMessages,
  getValidSolveForOptionsForContractType
} from "../../../../../utils/quoteUtils";
import {
  getFirstPaymentDateErrorMessage,
  getNumberOfPaymentsErrorMessage,
  interestRateErrorMessage,
  isInterestRateValid,
  isNumberOfPaymentsValid,
  startOfDay
} from "../../../../../utils/LoanCalcUtils";
import QuoterError from "../../../QuoterError/QuoterError";
import {getEmployeeName} from "../../../../../utils/utils";

export interface FinancingEditorProps {
  selectedFinanceLoanClass: FinanceLoanClass | undefined;
  selectedFinanceQuote: FinanceQuote;
  onChangeSingleField: <K extends keyof FinanceQuote>(fieldName: K, value: FinanceQuote[K], applicationQuoteId?: string) => void;
  currentApp: IApplication;
  financeLoanClasses: FinanceLoanClass[];
  applicationFinanceErrors: IRuleViolation[];
  configuration: IConfiguration;
  lookups: ILookupItems;
  financeQuoteFieldIsDisabled: (fieldName: string) => boolean;
  onChangeSelectedQuote: (selectedFinanceQuoteId: string) => void;
  employees: IEmployee[];
}

export default function FinancingEditor(props: FinancingEditorProps) {
  const financeQuotes = props.currentApp.financeQuotes.filter(afq => !hasValue(afq.deletedDate));
  const { employees } = props;
  const contractTypes = getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, "ContractType", props.selectedFinanceQuote.financeLoanClassId);
  const selectedContractType = getEnumForValue(contractTypes, props.selectedFinanceQuote.contractType);
  const paymentFrequencies = getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, "PaymentFrequency", props.selectedFinanceQuote.financeLoanClassId);
  const selectedPaymentFrequency = getEnumForValue(paymentFrequencies, props.selectedFinanceQuote.paymentFrequency);
  const allSolveForOptions = getOptionsFromEnumAndLoanClass(props.lookups.carletonItems, "SolveFor", props.selectedFinanceQuote.financeLoanClassId);
  const solveForOptionsForContractType = getValidSolveForOptionsForContractType(selectedContractType, allSolveForOptions);
  const selectedSolveFor = getEnumForValue(solveForOptionsForContractType, props.selectedFinanceQuote.solveFor);

  const interestRateIsValid = isInterestRateValid(props.selectedFinanceQuote.rate, props.selectedFinanceLoanClass?.minimumRate, props.selectedFinanceLoanClass?.maximumRate);
  const interestRateMessage = interestRateErrorMessage(props.selectedFinanceLoanClass?.minimumRate, props.selectedFinanceLoanClass?.maximumRate);
  
  const numberOfPaymentsIsValid = isNumberOfPaymentsValid(props.selectedFinanceQuote.numberOfPayments, props.selectedFinanceQuote.contractType, props.selectedFinanceQuote.solveFor);
  const numberOfPaymentsMessage = getNumberOfPaymentsErrorMessage(props.selectedFinanceQuote.numberOfPayments, props.selectedFinanceQuote.contractType, props.selectedFinanceQuote.solveFor);
  
  const firstPaymentDateErrors = getFirstPaymentDateErrorMessage(props.selectedFinanceQuote.firstPaymentDate, props.selectedFinanceQuote.contractDate, props.selectedFinanceQuote.interestStartDate);
  const firstPaymentDateMin = startOfDay(new Date(props.selectedFinanceQuote.contractDate));
  
  const errorMessages = getFinanceQuoteErrorMessages(props.applicationFinanceErrors);
  const usingIrregularPayments = props.selectedFinanceQuote.nonUniformPaymentOption === FinanceQuoteNonUniformPaymentOption.IrregularPayments;
  
  // Options for the non-uniform payment dropdown
  const nonUniformPaymentOptions = [
    { value: FinanceQuoteNonUniformPaymentOption.DeferredDown, label: 'Deferred Down' },
    { value: FinanceQuoteNonUniformPaymentOption.IrregularPayments, label: 'Irregular Payments' },
  ];
  const selectedNonUniformPaymentOption = nonUniformPaymentOptions.find(
    opt => opt.value === props.selectedFinanceQuote.nonUniformPaymentOption
  );

  const paymentLabel = usingIrregularPayments 
  ? 'Regular Payment' 
  : (props.selectedFinanceQuote.numberOfRegularPayments ?? 0) > 0 ? `${props.selectedFinanceQuote.numberOfRegularPayments} Payments of` : 'Payment';

  return (
    <div className={styles.financingEditorContainer}>
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={props.financeQuoteFieldIsDisabled("contractType") || usingIrregularPayments}
          valid={!hasValue(errorMessages.contractType)}
          label={'Contract Type'}
          data={contractTypes}
          textField="option"
          suggest={true}
          clearButton={false}
          value={selectedContractType}
          onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField('contractType', event.value.value)}
        />
        <QuoterError message={errorMessages.contractType}/>
      </div>
      <div>
        {financeQuotes.length > 1 && financeQuotes.map((quoteOption, key) => {
          return (
            <FinanceQuoterDownPaymentOption
              key={key}
              {...props}
              selectedFinanceQuoteId={props.selectedFinanceQuote.id}
              quoteOption={quoteOption}
              downPaymentErrorMessage={errorMessages.downPayment}
              paymentErrorMessage={errorMessages.payment}
            />
          )
        })}
      </div>
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={props.financeQuoteFieldIsDisabled("solveFor")}
          valid={!hasValue(errorMessages.solveFor)}
          label={'Solve For'}
          data={solveForOptionsForContractType}
          textField="option"
          suggest={true}
          clearButton={false}
          value={selectedSolveFor}
          onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField('solveFor', event.value.value)}
        />
        <QuoterError message={errorMessages.solveFor}/>
      </div>
      {props.configuration.featureFlags[FeatureFlagIds.UseMultiplePaymentStreamsWithAnyLoanClass] === true &&
        <div className={styles.sectionItem}>
          <ComboBox
            disabled={props.financeQuoteFieldIsDisabled("nonUniformPaymentOption")}
            label={'Non-Uniform Payment Option'}
            data={nonUniformPaymentOptions}
            textField="label"
            suggest={true}
            clearButton={false}
            value={selectedNonUniformPaymentOption}
            onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField('nonUniformPaymentOption', event.value.value)}
          />
        </div>
      }
      <div className={styles.sectionItem}>
        <DatePickerWithYearSanitizer
          disabled={props.financeQuoteFieldIsDisabled("firstPaymentDate")}
          valid={!hasValue(errorMessages.firstPaymentDate)}
          label={'First Payment Date'}
          value={new Date(props.selectedFinanceQuote.firstPaymentDate)}
          min={firstPaymentDateMin}
          onChange={(event: DatePickerChangeEvent) => props.onChangeSingleField('firstPaymentDate', event.value ?? props.selectedFinanceQuote.firstPaymentDate)}
        />
        {firstPaymentDateErrors.length > 0 && firstPaymentDateErrors.map((e, key) => <Error key={key}>{e}</Error>)}
        <QuoterError message={errorMessages.firstPaymentDate}/>
      </div>
      <div className={styles.sectionItem}>
        <DatePickerWithYearSanitizer
          disabled={props.financeQuoteFieldIsDisabled("interestStartDate")}
          valid={!hasValue(errorMessages.interestStartDate)}
          label={'Interest Start Date'}
          value={new Date(props.selectedFinanceQuote.interestStartDate)}
          onChange={(event: DatePickerChangeEvent) => props.onChangeSingleField('interestStartDate', event.value ?? new Date())}
        />
        <QuoterError message={errorMessages.interestStartDate}/>
      </div>
      <div className={styles.sectionItem}>
        <NoScrollNumericTextBox
          readOnly={props.financeQuoteFieldIsDisabled("termInMonths")}
          valid={!hasValue(errorMessages.termInMonths)}
          label={'Term in Months'}
          value={props.selectedFinanceQuote.termInMonths}
          spinners={false}
          min={1}
          onChange={(e: NumericTextBoxChangeEvent) => props.onChangeSingleField('termInMonths', Number(e.target.value))}
          style={getBackgroundColorForPotentialSolveForFields("termInMonths", props.selectedFinanceQuote.solveFor)}
        />
        <QuoterError message={errorMessages.termInMonths}/>
      </div>
      <div className={styles.sectionItem}>
        <NoScrollNumericTextBox
          readOnly={props.financeQuoteFieldIsDisabled("numberOfPayments")}
          valid={numberOfPaymentsIsValid && !hasValue(errorMessages.numberOfPayments)}
          label={'Number of Payments'}
          value={props.selectedFinanceQuote.numberOfPayments}
          spinners={false}
          min={1}
          onChange={(e: NumericTextBoxChangeEvent) => props.onChangeSingleField('numberOfPayments', Number(e.target.value))}
          style={getBackgroundColorForPotentialSolveForFields("numberOfPayments", props.selectedFinanceQuote.solveFor)}
        />
        {!numberOfPaymentsIsValid && <Error>{numberOfPaymentsMessage}</Error>}
        <QuoterError message={errorMessages.numberOfPayments}/>
      </div>
      <div className={styles.sectionItem}>
        <NoScrollNumericTextBox
          readOnly={props.financeQuoteFieldIsDisabled("rate")}
          valid={interestRateIsValid && !hasValue(errorMessages.rate)}
          label={'Interest Rate'}
          value={props.selectedFinanceQuote?.rate ?? 0}
          spinners={false}
          onChange={(e: NumericTextBoxChangeEvent) => props.onChangeSingleField('rate', Number(e.target.value))}
          style={getBackgroundColorForPotentialSolveForFields("rate", props.selectedFinanceQuote.solveFor)}
        />
        {!interestRateIsValid && <Error>{interestRateMessage}</Error>}
        <QuoterError message={errorMessages.rate}/>
      </div>
    
      <div className={styles.sectionItem}>
        <ComboBox
          disabled={props.financeQuoteFieldIsDisabled("paymentFrequency")}
          valid={!hasValue(errorMessages.paymentFrequency)}
          label={'Pay Frequency'}
          data={paymentFrequencies}
          textField="option"
          suggest={true}
          clearButton={false}
          value={selectedPaymentFrequency}
          onChange={(event: ComboBoxChangeEvent) => props.onChangeSingleField('paymentFrequency', event.value.value)}
        />
        <QuoterError message={errorMessages.paymentFrequency}/>
      </div>
      <div className={styles.sectionItem}>
        <NoScrollNumericTextBox
          readOnly={props.financeQuoteFieldIsDisabled("payment")}
          valid={!hasValue(errorMessages.payment)}
          label={paymentLabel}
          spinners={false}
          format="c2"
          value={props.selectedFinanceQuote.payment}
          onChange={(e: NumericTextBoxChangeEvent) => props.onChangeSingleField('payment', Number(e.target.value))}
          style={getBackgroundColorForPotentialSolveForFields("payment", props.selectedFinanceQuote.solveFor)}
        />
        <QuoterError message={errorMessages.payment}/>
      </div>
      {(props.selectedFinanceQuote.balloonFinalPayment ?? 0) > 0 &&
          <div className={styles.sectionItem}>
              <NoScrollNumericTextBox
                  readOnly
                  format="c2"
                  spinners={false}
                  label={props.selectedFinanceQuote.balloonFinalPayment ?? 0 > 0 ? `1 Final Payment of` : 'Payment'}
                  value={props.selectedFinanceQuote.balloonFinalPayment}
              />
              <QuoterError message={errorMessages.payment}/>
          </div>
      }
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.amountFinanced)}
          label={'Finance Amount'}
          value={toDollars(props.selectedFinanceQuote.amountFinanced)}
          type='text'
        />
        <QuoterError message={errorMessages.amountFinanced}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.financeCharge)}
          label={'Finance Charges'}
          value={toDollars(props.selectedFinanceQuote.financeCharge)}
          type='text'
        />
        <QuoterError message={errorMessages.financeCharge}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.totalOfPayments)}
          label={'Total of Payments'}
          value={toDollars(props.selectedFinanceQuote.totalOfPayments)}
          type='text'
        />
        <QuoterError message={errorMessages.totalOfPayments}/>
      </div>
      <div className={styles.sectionItem}>
        <Input
          readOnly
          valid={!hasValue(errorMessages.apr)}
          label={'APR'}
          value={props.selectedFinanceQuote.apr}
          type='number'
        />
        <QuoterError message={errorMessages.apr}/>
      </div>
      {hasValue(props.selectedFinanceQuote.carletonExceptionMessage) &&
          <div className={styles.sectionItem}>
              <Error style={{color: "red", fontSize: 12}}>Carleton Raw Error Message</Error>
              <TextArea
                  readOnly
                  autoSize
                  valid={false}
                  value={props.selectedFinanceQuote.carletonExceptionMessage}
              />
          </div>
      }
      {props.selectedFinanceQuote.carletonQuoteErrorMessage &&
          <div>
              <div className={styles.sectionItem}>
                  <Input
                      readOnly
                      valid={false}
                      label={'Carleton Error'}
                      value={`#${props.selectedFinanceQuote.carletonQuoteErrorMessage.code}- ${props.selectedFinanceQuote.carletonQuoteErrorMessage.description}`}
                      type='text'
                  />
              </div>
              <div className={styles.sectionItem}>
                  <Error style={{color: "red", fontSize: 12}}>Troubleshooting Steps</Error>
                  <TextArea
                      readOnly
                      autoSize
                      valid={false}
                      value={`${props.selectedFinanceQuote.carletonQuoteErrorMessage.troubleshootingSteps}`}
                  />
              </div>
          </div>
      }
      {props.selectedFinanceQuote.quoteGeneratedBy?.length && props.selectedFinanceQuote.quoteGeneratedDate &&
          <div className={styles.sectionItem}>
              <Input
                  readOnly
                  label={'Quote Generated By'}
                  value={`${getEmployeeName(employees, props.selectedFinanceQuote.quoteGeneratedBy)} at ${new Date(props.selectedFinanceQuote.quoteGeneratedDate).toLocaleString()}`}
                  type='text'
              />
          </div>
      }
    </div>
  );
}
